// Files brought into the draft: chosen with Import, pasted, or dropped. Text and Markdown come
// in as they're written; web pages and Word documents with their formatting, as Markdown. Images
// are sent to have their text read, or to be described, when that service is available. Drafts
// opened from a file are only ever text or Markdown (text-file.ts), as they're saved back to it.

import {
  IMAGE_TEXT_MEDIA_TYPES,
  MAX_IMAGE_TEXT_BYTES,
  type ImageMode,
  type ImageTextQuery,
} from '@lyrmo/shared';
import { ApiError } from '../../api/api-error';
import { docxHtml } from './docx';
import { markdownFromHtml } from './markdown-html';
import { readTextFile } from './text-file';

const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** What the file picker offers. */
export const IMPORT_ACCEPT = [
  '.txt',
  '.md',
  '.markdown',
  '.html',
  '.htm',
  '.docx',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  'text/plain',
  'text/markdown',
  'text/html',
  DOCX_TYPE,
  ...IMAGE_TEXT_MEDIA_TYPES,
].join(',');

type ImportKind = 'text' | 'html' | 'docx' | 'image';

const IMAGE_EXTENSIONS: Readonly<Record<string, string>> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
};

function imageType(file: File): string | undefined {
  if ((IMAGE_TEXT_MEDIA_TYPES as readonly string[]).includes(file.type)) return file.type;
  // Some file pickers omit the MIME type; the server still verifies the actual image bytes.
  if (!file.type || file.type === 'application/octet-stream') {
    return IMAGE_EXTENSIONS[file.name.toLowerCase().split('.').at(-1) ?? ''];
  }
  return undefined;
}

export function isImageImport(file: File): boolean {
  return imageType(file) !== undefined;
}

// The largest file of each kind read. A Word document's pictures make it larger than its writing.
const MAX_BYTES: Readonly<Record<ImportKind, number>> = {
  text: 256 * 1024,
  html: 2 * 1024 * 1024,
  docx: 20 * 1024 * 1024,
  image: MAX_IMAGE_TEXT_BYTES,
};
// The most writing a file brings, as much as the largest text file.
const MAX_CHARACTERS = 256 * 1024;

function importKind(file: File): ImportKind | null {
  const name = file.name.toLowerCase();
  if (isImageImport(file)) return 'image';
  if (name.endsWith('.docx') || file.type === DOCX_TYPE) return 'docx';
  if (/\.html?$/u.test(name) || file.type === 'text/html') return 'html';
  if (/\.(?:txt|text|md|markdown|csv|tsv|log)$/u.test(name)) return 'text';
  // Rich text is text, but written in a language of its own.
  return file.type.startsWith('text/') && file.type !== 'text/rtf' ? 'text' : null;
}

/** Whether `file` is one the draft can take, before it's read. */
export function canImport(file: File): boolean {
  return importKind(file) !== null;
}

/** Why `file` can't be imported, before reading it, or null when it can be. */
export function importProblem(file: File): 'wrongImportType' | 'importTooLarge' | null {
  const kind = importKind(file);
  if (!kind) return 'wrongImportType';
  return file.size > MAX_BYTES[kind] ? 'importTooLarge' : null;
}

/** A file's writing, or why there's none to take. */
export type Imported =
  { readonly text: string } | { readonly problem: 'unreadableFile' | 'importTooLarge' };

/** Reads an image's text, or with `mode: 'describe'` describes it in `language`. */
export type ExtractImageText = (
  image: Blob,
  options?: { signal?: AbortSignal } & Partial<ImageTextQuery>,
) => Promise<string>;

/**
 * The file's writing, as Markdown, with its lines ended as the draft's are; or the problem: it has
 * none that's readable, or more than the draft could ever take in. An image's writing is its text,
 * or with `imageMode: 'describe'`, a description of it.
 */
export async function readImport(
  file: File,
  {
    extractImageText,
    imageMode,
    language,
    signal,
  }: {
    extractImageText?: ExtractImageText | undefined;
    imageMode?: ImageMode | undefined;
    language?: ImageTextQuery['language'] | undefined;
    signal?: AbortSignal;
  } = {},
): Promise<Imported> {
  const kind = importKind(file);
  let text: string | null = null;
  if (kind === 'text') text = await readTextFile(file);
  else if (kind === 'html') text = markdownFromHtml(await file.text(), true);
  else if (kind === 'docx') {
    const html = await docxHtml(await file.arrayBuffer());
    text = html === null ? null : markdownFromHtml(html, true);
  } else if (kind === 'image') {
    if (!extractImageText) {
      throw new ApiError('SERVICE_UNAVAILABLE', 'Image text detection is not available here.');
    }
    const image = file.type === imageType(file) ? file : file.slice(0, file.size, imageType(file));
    text = await extractImageText(image, {
      ...(signal && { signal }),
      ...(imageMode && { mode: imageMode }),
      ...(language && { language }),
    });
  }
  if (!text?.trim()) return { problem: 'unreadableFile' };
  if (text.length > MAX_CHARACTERS) return { problem: 'importTooLarge' };
  return { text: text.replace(/\r\n?/gu, '\n') };
}
